import {
  getMerchantPartnerOnlinePricingPreviewFromServer,
  PartnerOnlinePricingPreview,
} from "../../../businessesApi";
import { IPartner } from "../../../../../interfaces/partner.interface";
import { getPartnerWithPricing } from "../../../../partners/partnersApi";

type CacheEntry<T> = {
  promise: Promise<T>;
  settled?: boolean;
  value?: T;
};

type Peek<T> = { hit: true; value: T } | { hit: false };

const partnerCache = new Map<string, CacheEntry<IPartner | undefined>>();
const previewCache = new Map<string, CacheEntry<PartnerOnlinePricingPreview>>();

export type OnlinePricingPreviewParams = {
  businessId: string;
  partnerId?: string;
  partnerOnlinePurchPriceProfileId?: string;
};

export function previewCacheKey({
  businessId,
  partnerId,
  partnerOnlinePurchPriceProfileId,
}: OnlinePricingPreviewParams): string {
  const profile =
    partnerId && partnerOnlinePurchPriceProfileId
      ? partnerOnlinePurchPriceProfileId
      : "";
  return `${businessId}|${partnerId ?? ""}|${profile}`;
}

function peekEntry<T>(cache: Map<string, CacheEntry<T>>, key: string): Peek<T> {
  const entry = cache.get(key);
  if (!entry?.settled) return { hit: false };
  return { hit: true, value: entry.value as T };
}

export function clearPricingPrefetchCache(): void {
  partnerCache.clear();
  previewCache.clear();
}

export function peekPartnerWithPricing(
  partnerId: string
): Peek<IPartner | undefined> {
  return peekEntry(partnerCache, partnerId);
}

export function peekOnlinePricingPreview(
  params: OnlinePricingPreviewParams
): Peek<PartnerOnlinePricingPreview> {
  return peekEntry(previewCache, previewCacheKey(params));
}

function remember<T>(
  cache: Map<string, CacheEntry<T>>,
  key: string,
  load: () => Promise<T>
): Promise<T> {
  const existing = cache.get(key);
  if (existing) return existing.promise;
  const entry: CacheEntry<T> = { promise: null as never };
  cache.set(key, entry);
  try {
    entry.promise = Promise.resolve(load())
      .then((value) => {
        entry.settled = true;
        entry.value = value;
        return value;
      })
      .catch((error) => {
        cache.delete(key);
        throw error;
      });
  } catch (error) {
    cache.delete(key);
    return Promise.reject(error);
  }
  return entry.promise;
}

export function loadPartnerWithPricing(
  partnerId: string
): Promise<IPartner | undefined> {
  return remember(partnerCache, partnerId, () =>
    getPartnerWithPricing(partnerId)
  );
}

export function loadOnlinePricingPreview(
  params: OnlinePricingPreviewParams
): Promise<PartnerOnlinePricingPreview> {
  return remember(previewCache, previewCacheKey(params), () =>
    getMerchantPartnerOnlinePricingPreviewFromServer({
      businessId: params.businessId,
      ...(params.partnerId ? { partnerId: params.partnerId } : {}),
      ...(params.partnerId && params.partnerOnlinePurchPriceProfileId
        ? {
            partnerOnlinePurchPriceProfileId:
              params.partnerOnlinePurchPriceProfileId,
          }
        : {}),
    })
  );
}

/** Start Pricing (New) reads with the editor. The pane itself is lazy. */
export function prefetchPricingTabData({
  businessId,
  partnerId,
  partnerOnlinePurchPriceProfileId,
  preview,
}: {
  businessId?: string;
  partnerId?: string;
  partnerOnlinePurchPriceProfileId?: string;
  preview: boolean;
}): void {
  if (partnerId) void loadPartnerWithPricing(partnerId).catch(() => {});
  if (preview && businessId) {
    void loadOnlinePricingPreview({
      businessId,
      partnerId,
      partnerOnlinePurchPriceProfileId,
    }).catch(() => {});
  }
}
